/**
 * Chapter009.js — Personal Protective Equipment (OHSE 009)
 * Migrated from SWMS Platform Chapter009.tsx → JavaScript
 *
 * Props:
 *   data      {object}  ppe dict from swmsData
 *   onChange  {fn}      (newPPE) => void
 */

import { useCallback } from 'react';
import { Plus, Trash2 } from 'lucide-react';

const PPE_ITEMS = [
  { key: 'steel_capped_boots',     label: 'Steel Capped Boots',       note: 'Always required' },
  { key: 'hi_visibility_clothing', label: 'High Visibility Clothing', note: 'Always required' },
  { key: 'hard_hat',               label: 'Hard Hat',                 note: 'As required' },
  { key: 'dust_mask',              label: 'Dust Mask',                note: 'As required' },
  { key: 'face_shield',            label: 'Face Shield',              note: 'When using angle grinders' },
  { key: 'gloves',                 label: 'Gloves',                   note: 'As required' },
  { key: 'safety_glasses',         label: 'Safety Glasses',           note: 'As required' },
  { key: 'hearing_protection',     label: 'Hearing Protection',       note: 'As required' },
];

const inputCls = 'w-full px-2 py-1.5 text-sm bg-[var(--theme-bg-secondary)] border border-[var(--theme-border)] rounded text-[var(--theme-text-primary)] focus:outline-none focus:border-[var(--theme-accent)]';

export default function Chapter009({ data = {}, onChange }) {
  const togglePPE = useCallback((key) => {
    onChange({ ...data, [key]: !data[key] });
  }, [data, onChange]);

  const register = data.ppe_register || [];

  const addEntry = () => {
    onChange({
      ...data,
      ppe_register: [...register, { id: crypto.randomUUID(), employee_name: '', date: '', notes: '' }],
    });
  };

  const updateEntry = (idx, field, value) => {
    const updated = [...register];
    updated[idx] = { ...updated[idx], [field]: value };
    onChange({ ...data, ppe_register: updated });
  };

  const removeEntry = (idx) => {
    onChange({ ...data, ppe_register: register.filter((_, i) => i !== idx) });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <p className="text-xs font-semibold text-[#c9a84c] uppercase tracking-widest mb-1">OHSE 009</p>
        <h2 className="text-xl font-bold text-[#1a1a2e] uppercase tracking-wide">
          Personal Protective Equipment (PPE)
        </h2>
      </div>

      {/* Section A: PPE Checklist */}
      <div className="bg-white border border-[#e0dbd0] rounded-lg overflow-hidden">
        <div className="px-4 py-2.5 bg-[#f0ede6]">
          <h3 className="text-sm font-semibold text-[#1a1a2e] uppercase tracking-wide">PPE Requirements</h3>
          <p className="text-xs text-gray-500 mt-0.5">
            Select all PPE items required for this project. Items marked "Always required" should remain checked.
          </p>
        </div>
        <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-3">
          {PPE_ITEMS.map(item => (
            <label
              key={item.key}
              className={`flex items-center gap-3 p-3 rounded-md border transition-colors cursor-pointer ${
                data[item.key]
                  ? 'border-[#c9a84c] bg-[rgba(201,168,76,0.12)]'
                  : 'border-[#e0dbd0] bg-white hover:bg-[#f8f7f4]'
              }`}
            >
              <input
                type="checkbox"
                checked={!!data[item.key]}
                onChange={() => togglePPE(item.key)}
                className="w-4 h-4 rounded border-[#c9a84c] accent-[#c9a84c] cursor-pointer"
              />
              <div className="flex-1">
                <span className="text-sm font-medium text-[#1a1a2e]">{item.label}</span>
                <span className="block text-xs text-gray-500">{item.note}</span>
              </div>
              {data[item.key] && (
                <span className="text-xs font-bold text-[#c9a84c]">✓</span>
              )}
            </label>
          ))}
        </div>
        <div className="px-4 pb-4">
          <label className="block text-xs text-gray-400 mb-1">Other PPE (specify)</label>
          <input
            type="text"
            value={data.other || ''}
            onChange={e => onChange({ ...data, other: e.target.value })}
            className={inputCls}
            placeholder="Describe any additional PPE requirements"
          />
        </div>
      </div>

      {/* Section B: PPE Register */}
      <div className="bg-white border border-[#e0dbd0] rounded-lg overflow-hidden">
        <div className="px-4 py-2.5 bg-[#f0ede6]">
          <h3 className="text-sm font-semibold text-[#1a1a2e] uppercase tracking-wide">PPE Register</h3>
          <p className="text-xs text-gray-500 mt-0.5">
            Record of PPE issued to employees — each employee has been instructed in correct PPE use.
          </p>
        </div>
        <div className="p-4">
          {register.length === 0 ? (
            <div className="py-8 text-center">
              <p className="text-sm text-gray-400 mb-4">No PPE register entries yet.</p>
              <button type="button" onClick={addEntry}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-[#1a1a2e] rounded hover:bg-[rgba(201,168,76,0.15)] transition-colors">
                <Plus className="w-4 h-4" /> Add Entry
              </button>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="bg-[#1a1a2e] text-white">
                      <th className="px-3 py-2.5 text-left text-xs uppercase tracking-wide w-[35%]">Employee Name</th>
                      <th className="px-3 py-2.5 text-left text-xs uppercase tracking-wide w-[25%]">Date of Issue</th>
                      <th className="px-3 py-2.5 text-left text-xs uppercase tracking-wide w-[35%]">Notes</th>
                      <th className="px-3 py-2.5 w-[5%]"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {register.map((entry, idx) => (
                      <tr key={entry.id} className="border-b border-[#e0dbd0] bg-white">
                        <td className="px-3 py-2">
                          <input type="text" value={entry.employee_name || ''} placeholder="Employee name"
                            onChange={e => updateEntry(idx, 'employee_name', e.target.value)}
                            className={inputCls} />
                        </td>
                        <td className="px-3 py-2">
                          <input type="date" value={entry.date || ''}
                            onChange={e => updateEntry(idx, 'date', e.target.value)}
                            className={inputCls} />
                        </td>
                        <td className="px-3 py-2">
                          <input type="text" value={entry.notes || ''} placeholder="Notes"
                            onChange={e => updateEntry(idx, 'notes', e.target.value)}
                            className={inputCls} />
                        </td>
                        <td className="px-3 py-2 text-center">
                          <button type="button" onClick={() => removeEntry(idx)}
                            className="text-red-400 hover:text-red-600 transition-colors">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="mt-4">
                <button type="button" onClick={addEntry}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-[#1a1a2e] rounded hover:bg-[rgba(201,168,76,0.15)] transition-colors">
                  <Plus className="w-4 h-4" /> Add Entry
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
